import type { DocumentListRecord } from "../types";

/**
 * The register's status vocabulary, in one place.
 *
 * The badge, the status filter, the per-row action cluster and the edit dialog
 * all have to agree on what a row *is* — and "part paid" exists nowhere in the
 * database: it is derived from the payments total, never stored (DEC-QI-013).
 * Deriving it in four components would be four chances to disagree.
 */
export type RegisterState =
	| "draft"
	| "sent"
	| "part-paid"
	| "paid"
	| "accepted"
	| "rejected"
	| "withdrawn"
	| "expired";

/** The badge variants the register uses, named for the primitive behind each. */
export type BadgeVariant = "neutral" | "accent" | "warning" | "strong" | "danger";

/** What this row is, as the register speaks about it. */
export function registerState(document: DocumentListRecord): RegisterState {
	if (document.expired) return "expired";
	if (document.kind === "invoice" && document.status === "sent") {
		const paid = document.paidCents ?? 0;
		if (paid > 0 && paid < document.totalCents) return "part-paid";
		return "sent";
	}
	switch (document.status) {
		case "draft":
		case "sent":
		case "paid":
		case "accepted":
		case "rejected":
		case "withdrawn":
			return document.status;
		default:
			// A status added server-side without touching this file still reads as
			// itself rather than disappearing from the table.
			return "draft";
	}
}

const LABELS: Record<RegisterState, string> = {
	draft: "Draft",
	sent: "Sent",
	"part-paid": "Part paid",
	paid: "Paid",
	accepted: "Accepted",
	rejected: "Rejected",
	withdrawn: "Withdrawn",
	expired: "Expired",
};

export function stateLabel(state: RegisterState): string {
	return LABELS[state];
}

const VARIANTS: Record<RegisterState, BadgeVariant> = {
	draft: "neutral",
	sent: "accent",
	"part-paid": "warning",
	paid: "strong",
	accepted: "strong",
	rejected: "neutral",
	withdrawn: "neutral",
	expired: "danger",
};

export function stateBadgeVariant(state: RegisterState): BadgeVariant {
	return VARIANTS[state];
}

/**
 * Amounts are editable until the first payment lands — not until the invoice is
 * sent. A sent invoice can still be corrected, behind a confirmation that says
 * the client may already be holding a copy (DEC-QI-013).
 */
export function canEditAmounts(document: DocumentListRecord): boolean {
	if (document.kind !== "invoice") return false;
	if (document.status === "draft") return true;
	return document.status === "sent" && (document.paidCents ?? 0) === 0;
}

/** Notes are commentary, not an amount, so they stay editable for good. */
export function canEditNotes(document: DocumentListRecord): boolean {
	return document.kind === "invoice";
}

/** True once some but not all of the invoice has been paid. */
export function isPartPaid(document: DocumentListRecord): boolean {
	return registerState(document) === "part-paid";
}
